import type { DialogueKwEvaluationMessage } from 'components/Dialogue/types';
import { MessageExtendComplex } from '../MessageExtend/MessageExtendComplex';
import { BaseMsgProps } from './types';
import QuoteMessage from '../QuoteMessage';
import { useChatContext } from '../../contexts/ChatContext';
import { memo } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Rate } from 'antd';

const EVALUATION_TAGS: Record<number, string[]> = {
  1: [
    'scene.chatWorkspace.messageThread.evaluation.tag.slowResponse',
    'scene.chatWorkspace.messageThread.evaluation.tag.badAttitude',
    'scene.chatWorkspace.messageThread.evaluation.tag.unresolved',
    'scene.chatWorkspace.messageThread.evaluation.tag.irrelevantAnswer',
  ],
  2: [
    'scene.chatWorkspace.messageThread.evaluation.tag.slowResponse',
    'scene.chatWorkspace.messageThread.evaluation.tag.badAttitude',
    'scene.chatWorkspace.messageThread.evaluation.tag.unresolved',
    'scene.chatWorkspace.messageThread.evaluation.tag.irrelevantAnswer',
  ],
  3: [
    'scene.chatWorkspace.messageThread.evaluation.tag.averageCommunication',
    'scene.chatWorkspace.messageThread.evaluation.tag.averageAttitude',
    'scene.chatWorkspace.messageThread.evaluation.tag.averageSolution',
    'scene.chatWorkspace.messageThread.evaluation.tag.averageEfficiency',
  ],
  4: [
    'scene.chatWorkspace.messageThread.evaluation.tag.smoothCommunication',
    'scene.chatWorkspace.messageThread.evaluation.tag.helpfulAnswer',
    'scene.chatWorkspace.messageThread.evaluation.tag.friendlyAttitude',
    'scene.chatWorkspace.messageThread.evaluation.tag.fastResponse',
  ],
  5: [
    'scene.chatWorkspace.messageThread.evaluation.tag.smoothCommunication',
    'scene.chatWorkspace.messageThread.evaluation.tag.helpfulAnswer',
    'scene.chatWorkspace.messageThread.evaluation.tag.friendlyAttitude',
    'scene.chatWorkspace.messageThread.evaluation.tag.fastResponse',
  ],
};

export const KwEvaluationMsg = memo((props: BaseMsgProps) => {
  const { raw, msg, conversation } = props;
  const { t } = useTranslate();
  const { clickQuote } = useChatContext();
  const { score, labelContent } = (msg as DialogueKwEvaluationMessage).content;
  const { topBarInfo } = conversation;

  return (
    <MessageExtendComplex
      {...props}
      content={
        <QuoteMessage
          message={raw}
          storeName={topBarInfo?.storeName}
          wrap
          onQuoteClick={clickQuote}
        >
          <Container>
            <header>
              {score
                ? t('scene.chatWorkspace.messageThread.feedback.thanks')
                : t('scene.chatWorkspace.messageThread.evaluation.question')}
            </header>
            <Rate disabled value={score} />
            {score ? (
              <div style={score <= 3 ? { color: '#F05B5B' } : undefined}>
                {
                  {
                    1: t('scene.chatWorkspace.messageThread.evaluation.rating.veryDissatisfied'),
                    2: t('scene.chatWorkspace.messageThread.evaluation.rating.dissatisfied'),
                    3: t('scene.chatWorkspace.messageThread.evaluation.rating.average'),
                    4: t('scene.chatWorkspace.messageThread.evaluation.rating.satisfied'),
                    5: t('scene.chatWorkspace.messageThread.evaluation.rating.verySatisfied'),
                  }[score]
                }
              </div>
            ) : undefined}
            {score ? (
              labelContent ? (
                <section>
                  <span style={{ color: '#B5B9BF' }}>{t(labelContent)}</span>
                </section>
              ) : (
                <section>
                  {EVALUATION_TAGS[score]?.map((label) => (
                    <span>{t(label)}</span>
                  ))}
                </section>
              )
            ) : null}
          </Container>
        </QuoteMessage>
      }
    />
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 390px;
  max-width: 100%;
  padding: 12px;
  background: #fff;
  border-radius: 2px;
  > header {
    align-self: flex-start;
  }
  > ul {
    display: flex;
    justify-content: space-around;
    width: 100%;
    padding: 0 24px;
  }
  > div {
    margin: 10px 0;
    color: #84868c;
  }
  > section {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    > span {
      height: 24px;
      line-height: 22px;
      margin: 0 6px 6px;
      padding: 0 12px;
      border-radius: 12px;
      border: solid 1px #f0f2f5;
      color: #515359;
    }
  }
`;
